import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Trash2, Copy, Link as LinkIcon, Image as ImageIcon, Upload, Eye,
} from "lucide-react";
import { copyToClipboard } from "@/lib/clipboard";

export default function BrandedLoginCard({ client, clientId, onChanged }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const brandedUrl = client?.login_code
    ? `${BACKEND}/c/${client.login_code}/login`
    : "";

  const heroInputRef = useRef();
  const [heroBusy, setHeroBusy] = useState(false);

  const copyBrandedUrl = async () => {
    if (!brandedUrl) return;
    const ok = await copyToClipboard(brandedUrl);
    toast[ok ? "success" : "error"](
      ok ? "Branded login URL copied" : "Couldn't copy — select the link and copy manually"
    );
  };

  const pickHero = () => heroInputRef.current?.click();

  const onHeroPicked = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Image must be 10 MB or smaller");
      return;
    }
    setHeroBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/clients/${clientId}/login-hero`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Login background updated");
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setHeroBusy(false);
      if (heroInputRef.current) heroInputRef.current.value = "";
    }
  };

  const clearHero = async () => {
    setHeroBusy(true);
    try {
      await api.delete(`/clients/${clientId}/login-hero`);
      toast.success("Background removed");
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setHeroBusy(false); }
  };

  if (!client) return null;

  return (
    <Card className="rounded-sm border-[#002FA7]/30 bg-[#F4F5FB]"
      data-testid="branded-login-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="h-9 w-9 bg-[#002FA7] text-white rounded-sm flex items-center justify-center flex-shrink-0">
            <LinkIcon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-overline">Branded login URL</div>
            <p className="text-xs text-zinc-500 mt-0.5">
              Share this URL with your client contacts. They&apos;ll see the
              <strong> {client.company_name}</strong> logo and brand colours on the
              sign-in page.
            </p>
          </div>
          <code
            className="font-mono text-xs text-zinc-800 bg-white border border-zinc-200 rounded-sm px-2.5 py-1.5 truncate max-w-[480px]"
            data-testid="branded-login-url"
            title={brandedUrl}
          >
            {brandedUrl}
          </code>
          <Button
            size="sm"
            onClick={copyBrandedUrl}
            className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white h-9"
            data-testid="branded-login-copy"
          >
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
          </Button>
          <a
            href={brandedUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs label-overline text-[#002FA7] hover:underline"
            data-testid="branded-login-open"
          >
            Open ↗
          </a>
          <Link
            to={`/portal/dashboard?client_id=${encodeURIComponent(clientId)}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[12px] font-medium rounded-sm border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors px-3 h-9"
            data-testid="branded-login-preview-dashboard"
            title="View this client's dashboard with admin credentials — no need to log out"
          >
            <Eye className="h-3.5 w-3.5" /> View dashboard
          </Link>
        </div>

        {/* Hero background image picker */}
        <div className="flex items-stretch gap-3 flex-wrap pt-3 border-t border-[#002FA7]/15">
          <div
            className="w-44 h-24 rounded-sm overflow-hidden border border-zinc-200 bg-white flex-shrink-0 relative"
            style={client.login_hero_image_url ? {
              backgroundImage: `url(${BACKEND}${client.login_hero_image_url})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            } : undefined}
            data-testid="branded-login-hero-preview"
          >
            {!client.login_hero_image_url && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-400">
                <ImageIcon className="h-6 w-6" strokeWidth={1.5} />
                <span className="text-[10px] uppercase tracking-wider mt-1">No image</span>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <div className="label-overline">Login page background</div>
            <p className="text-xs text-zinc-500 mt-1 max-w-md">
              Upload a wide photo (landscape, ideally ≥ 1600×900) that appears as
              the background on the left side of the branded sign-in page. JPG or
              PNG, up to 10 MB.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <input ref={heroInputRef} type="file" accept="image/*" className="hidden"
              onChange={onHeroPicked} data-testid="branded-login-hero-input" />
            <Button
              size="sm"
              onClick={pickHero}
              disabled={heroBusy}
              className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white h-9"
              data-testid="branded-login-hero-upload"
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              {client.login_hero_image_url ? "Replace" : "Upload"}
            </Button>
            {client.login_hero_image_url && (
              <Button
                size="sm"
                variant="ghost"
                onClick={clearHero}
                disabled={heroBusy}
                className="rounded-sm text-zinc-700 h-9"
                data-testid="branded-login-hero-clear"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
